import { ceilPositiveMinutesToStep } from "../../../../../_shared/_logic/time-granularity"

/** Multi-select value that stands for the free-text order type ("Sonstige"). */
export const CUSTOM_ORDER_TYPE_SELECTION = "__custom__"

/**
 * Order type choice of a work order draft: either an ordered catalog selection or one free-text
 * type. A non-null `custom_order_type` marks free-text mode even while its labels are still empty.
 */
export type OrderTypeSelection = {
  order_type_ids: readonly string[]
  custom_order_type: WorkOrderCustomOrderType | null
}

type OrderTypeDurationSource = Pick<OrderType, "id" | "typical_duration_min">
type OrderTypeLabelSource = Pick<OrderType, "id" | "internal_label" | "public_label"> & {
  description?: string | null
}

const clean = (value: unknown) => `${value ?? ""}`.trim()

const uniqueValues = (values: readonly unknown[]): string[] =>
  Array.from(new Set(values.map(clean).filter(Boolean)))

/**
 * Reconciles a changed multi-select value with the previous one.
 *
 * Kept catalog ids keep their order and newly chosen ids are appended, because position 0 defines
 * the customer instruction. Choosing free text clears catalog ids; choosing a catalog id while free
 * text is active clears the free text.
 */
export const reconcileOrderTypeSelection = (
  previous: readonly string[],
  incoming: readonly string[],
): string[] => {
  const previousValues = uniqueValues(previous)
  const incomingValues = uniqueValues(incoming)
  const choseCustom =
    incomingValues.includes(CUSTOM_ORDER_TYPE_SELECTION) &&
    (!previousValues.includes(CUSTOM_ORDER_TYPE_SELECTION) || incomingValues.length === 1)

  if (choseCustom) return [CUSTOM_ORDER_TYPE_SELECTION]

  const incomingIds = incomingValues.filter((value) => value !== CUSTOM_ORDER_TYPE_SELECTION)
  const keptIds = previousValues.filter((value) => incomingIds.includes(value))

  return [...keptIds, ...incomingIds.filter((value) => !keptIds.includes(value))]
}

/** Sum of the positive typical durations of the selected types, rounded up to the scheduling grid. */
export const summedTypicalDurationMinutes = (
  orderTypeIds: readonly string[],
  orderTypes: readonly OrderTypeDurationSource[],
): number | null => {
  const durationById = new Map(orderTypes.map((type) => [type.id, type.typical_duration_min]))
  const total = orderTypeIds.reduce((sum, id) => {
    const duration = Number(durationById.get(id))

    return Number.isFinite(duration) && duration > 0 ? sum + duration : sum
  }, 0)

  return ceilPositiveMinutesToStep(total)
}

/** Multi-select values representing a selection. */
export const orderTypeSelectionValues = (selection: OrderTypeSelection): string[] => {
  if (selection.order_type_ids.length) return [...selection.order_type_ids]

  return selection.custom_order_type ? [CUSTOM_ORDER_TYPE_SELECTION] : []
}

export const isCustomOrderTypeSelection = (values: readonly string[]) =>
  values.includes(CUSTOM_ORDER_TYPE_SELECTION)

/** Catalog ids of a multi-select value without the free-text marker. */
export const catalogOrderTypeIds = (values: readonly string[]): string[] =>
  uniqueValues(values).filter((value) => value !== CUSTOM_ORDER_TYPE_SELECTION)

/** Persisted selection of a work order, derived from its ordered `order_types` items. */
export const orderTypeSelectionFromWorkOrder = (
  workOrder: Pick<WorkOrder, "order_types"> | null | undefined,
): OrderTypeSelection & { order_type_ids: string[] } => {
  const items = workOrder?.order_types ?? []
  const orderTypeIds = uniqueValues(items.map((item) => item.order_type_id))
  const customItem = orderTypeIds.length ? null : items[0]

  return {
    order_type_ids: orderTypeIds,
    custom_order_type: customItem
      ? { internal_label: customItem.internal_label, public_label: customItem.public_label }
      : null,
  }
}

const customLabels = (custom: WorkOrderCustomOrderType | null) => ({
  internal: clean(custom?.internal_label),
  public: clean(custom?.public_label),
})

export const hasCompleteOrderTypeSelection = (selection: OrderTypeSelection) => {
  const labels = customLabels(selection.custom_order_type)

  return selection.order_type_ids.length > 0 || Boolean(labels.internal && labels.public)
}

export const sameOrderTypeSelection = (left: OrderTypeSelection, right: OrderTypeSelection) => {
  const leftLabels = customLabels(left.order_type_ids.length ? null : left.custom_order_type)
  const rightLabels = customLabels(right.order_type_ids.length ? null : right.custom_order_type)

  return (
    left.order_type_ids.length === right.order_type_ids.length &&
    left.order_type_ids.every((id, index) => id === right.order_type_ids[index]) &&
    leftLabels.internal === rightLabels.internal &&
    leftLabels.public === rightLabels.public
  )
}

/**
 * Request fields for a selection. Exactly one of both fields is emitted; an incomplete selection
 * emits none so the server keeps the stored order types on update.
 */
export const orderTypeSelectionPayload = (
  selection: OrderTypeSelection,
): Pick<WorkOrderCreatePayload, "order_type_ids" | "custom_order_type"> => {
  if (selection.order_type_ids.length) return { order_type_ids: [...selection.order_type_ids] }

  const labels = customLabels(selection.custom_order_type)

  if (!labels.internal || !labels.public) return {}

  return { custom_order_type: { internal_label: labels.internal, public_label: labels.public } }
}

/**
 * Display items of a not yet persisted selection, resolved against the catalog in selection order.
 * Lets drafts reuse the work order display rules before the server snapshots the labels.
 */
export const selectedOrderTypeItems = (
  selection: OrderTypeSelection,
  orderTypes: readonly OrderTypeLabelSource[],
): WorkOrderOrderTypeItem[] => {
  if (!selection.order_type_ids.length) {
    const labels = customLabels(selection.custom_order_type)

    return labels.internal
      ? [
          {
            order_type_id: null,
            internal_label: labels.internal,
            public_label: labels.public || labels.internal,
            description: null,
          },
        ]
      : []
  }

  const typeById = new Map(orderTypes.map((type) => [type.id, type]))

  return selection.order_type_ids.flatMap((id) => {
    const type = typeById.get(id)

    return type
      ? [
          {
            order_type_id: type.id,
            internal_label: type.internal_label,
            public_label: type.public_label,
            description: type.description ?? null,
          },
        ]
      : []
  })
}
